"use client";

import { type RefObject, useEffect, useRef } from "react";
import {
  registerSwipeBackTarget,
  type SwipeBackMode,
} from "@/lib/swipe-back/swipe-back-registry";

/**
 * Offer the edge-swipe-back gesture on this screen, committing through
 * `onBack` — pass the very handler the visible back affordance runs.
 *
 * `ref` is that affordance: the gesture is only offered while it is laid out,
 * so a back arrow hidden at `lg:` or inside a backgrounded route turns the
 * swipe off with it. `BackButton` already calls this; only hand-rolled back
 * arrows need to.
 *
 * `onBack` is read through a ref, so an inline closure never re-registers.
 */
export function useSwipeBackTarget(
  ref: RefObject<Element | null>,
  onBack: () => void,
  {
    enabled = true,
    mode = "navigate",
    previewHref,
  }: {
    enabled?: boolean;
    mode?: SwipeBackMode;
    previewHref?: string;
  } = {},
) {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const previewHrefRef = useRef(previewHref);
  previewHrefRef.current = previewHref;

  useEffect(() => {
    if (!enabled) return;
    return registerSwipeBackTarget({
      getElement: () => ref.current,
      onBack: () => onBackRef.current(),
      mode,
      getPreviewHref: () =>
        previewHrefRef.current ??
        ref.current?.closest("a")?.getAttribute("href") ??
        ref.current?.querySelector("a")?.getAttribute("href") ??
        null,
    });
  }, [enabled, mode, ref]);
}
